/**
 * precos.ts — Categoria "Preços" (10 modelos premium)
 *
 * Planos e valores apresentados dentro de seções em camadas, com selos,
 * títulos, observações e chamadas de ação editáveis.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para o bloco de planos. */
function planos(itens: string, colunas = '3', titulo = '', nota = '') {
  return b('page.pricing', { title: titulo, items: itens, columns: colunas, note: nota });
}

const TRES =
  'Essencial|R$ 149|por mês|1 usuário;Suporte por e-mail;Relatórios básicos|não|Assinar|#formulario\n' +
  'Profissional|R$ 299|por mês|5 usuários;Suporte prioritário;Relatórios completos;Automação|sim|Assinar|#formulario\n' +
  'Avançado|R$ 599|por mês|Usuários ilimitados;Gerente de conta;Integrações;Treinamento|não|Falar com vendas|#formulario';

const DOIS =
  'Mensal|R$ 299|por mês|Todos os recursos;Cancele quando quiser|não|Assinar|#formulario\n' +
  'Anual|R$ 249|por mês|Todos os recursos;2 meses grátis;Suporte prioritário|sim|Assinar|#formulario';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'precos.tres-planos',
    category: 'precos',
    label: 'Três planos',
    description: 'Comparativo clássico com o plano do meio em destaque.',
    preview: 'pricing',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Planos que crescem com você', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Sem fidelidade e sem taxa de implantação.', align: 'center', maxWidth: '560px' }),
        planos(TRES),
      ]),
    ],
  },

  {
    key: 'precos.dois-planos',
    category: 'precos',
    label: 'Mensal e anual',
    description: 'Duas opções lado a lado, destacando o desconto anual.',
    preview: 'pricing',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '960px', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.badge', { text: 'Economize 2 meses', variant: 'soft', align: 'center' }),
        planos(DOIS, '2', 'Escolha como prefere pagar'),
      ]),
    ],
  },

  {
    key: 'precos.escuro',
    category: 'precos',
    label: 'Planos em fundo escuro',
    description: 'Fundo escuro com brilho e três planos.',
    preview: 'pricing',
    outputs: WEB,
    order: 3,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '1120px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Um plano para cada momento', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          planos(TRES),
        ],
      ),
    ],
  },

  {
    key: 'precos.com-garantia',
    category: 'precos',
    label: 'Planos com garantia',
    description: 'Planos seguidos de um cartão de garantia.',
    preview: 'pricing',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade' }, [
        planos(TRES, '3', 'Escolha o seu plano', 'Valores em reais, impostos inclusos.'),
        b('ui.card', {
          icon: '🛡',
          title: 'Garantia de 30 dias',
          text: 'Não gostou? Devolvemos o valor integral, sem burocracia.',
          variant: 'outline',
          radius: 'xl',
          shadow: 'sm',
        }),
      ]),
    ],
  },

  {
    key: 'precos.com-prazo',
    category: 'precos',
    label: 'Planos com prazo de validade',
    description: 'Faixa de prazo em destaque acima dos planos.',
    preview: 'pricing',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', backgroundGradient: 'soft', motion: 'fade-up' }, [
        b('page.deadline', { title: 'Condição válida até o fim do mês', note: 'Depois desta data os valores podem mudar.' }),
        planos(DOIS, '2'),
      ]),
    ],
  },

  {
    key: 'precos.plano-unico',
    category: 'precos',
    label: 'Plano único em destaque',
    description: 'Uma oferta só, com lista de itens e botão grande.',
    preview: 'pricing',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '760px', radius: 'xl', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.title', { text: 'Tudo incluído, sem surpresa', size: 'lg', level: 'h2', align: 'center' }),
        planos('Completo|R$ 399|por mês|Usuários ilimitados;Todos os módulos;Suporte prioritário;Treinamento|sim|Começar agora|#formulario', '1'),
      ]),
    ],
  },

  {
    key: 'precos.com-perguntas',
    category: 'precos',
    label: 'Planos com dúvidas',
    description: 'Planos acompanhados das perguntas mais comuns sobre cobrança.',
    preview: 'pricing',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade-up' }, [
        planos(TRES, '3', 'Planos e valores'),
        b('page.faq', {
          title: 'Dúvidas sobre cobrança',
          columns: '2',
          items:
            'Posso trocar de plano depois?|Sim, a qualquer momento, com cobrança proporcional.\n' +
            'Tem fidelidade?|Não. Você pode cancelar quando quiser.\n' +
            'Quais formas de pagamento?|Boleto, cartão de crédito e Pix.',
        }),
      ]),
    ],
  },

  {
    key: 'precos.com-foto',
    category: 'precos',
    label: 'Planos com foto de apoio',
    description: 'Foto de ambiente ao lado da explicação e planos abaixo.',
    preview: 'pricing',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '1120px', layout: 'split', motion: 'fade' }, [
        b('ui.title', { text: 'Invista no que traz retorno', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Escolha o plano de acordo com o tamanho do time e evolua quando precisar.' }),
        b('ui.image', { src: FOTO.consultoria, alt: 'Reunião de negociação', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
        planos(DOIS, '2'),
      ]),
    ],
  },

  {
    key: 'precos.comparativo',
    category: 'precos',
    label: 'Comparativo com observação',
    description: 'Três planos e uma observação de rodapé bem visível.',
    preview: 'table',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', pattern: 'grid', motion: 'fade-up' }, [
        planos(TRES, '3', 'Compare os planos', 'Todos os planos incluem atualizações e suporte por e-mail.'),
      ]),
    ],
  },

  {
    key: 'precos.com-convite',
    category: 'precos',
    label: 'Planos com convite final',
    description: 'Planos seguidos de uma chamada para falar com o time.',
    preview: 'cta',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade' }, [
        planos(TRES),
        b('ui.paragraph', { text: 'Precisa de algo diferente? Montamos um plano sob medida.', align: 'center' }),
        b('ui.buttons', { items: 'Falar com um especialista|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
